import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaShieldAlt, FaBolt, FaCertificate, FaHeadset } from 'react-icons/fa'
import { useHomepageContent } from '../../hooks/useHomepageContent'

// ✅ "Niyə Itemzo?" — etibar + dəyər təklifi bölməsi (konversiya yönlü).
// Rəqəmlər uydurulmur; dəyər ifadələri istifadə olunur (100% / 24/7 / Ani / Rəsmi).

// Copy lives in home.why.* (locale); icons/gradients/values stay here.
const FEATURES = [
    { icon: <FaShieldAlt />, titleKey: 'home.why.features.secureTitle',  textKey: 'home.why.features.secureText',  grad: 'from-brand-500 to-indigo-600' },
    { icon: <FaBolt />,      titleKey: 'home.why.features.instantTitle', textKey: 'home.why.features.instantText', grad: 'from-accent-500 to-orange-600' },
    { icon: <FaCertificate />, titleKey: 'home.why.features.officialTitle', textKey: 'home.why.features.officialText', grad: 'from-emerald-500 to-teal-600' },
    { icon: <FaHeadset />,   titleKey: 'home.why.features.supportTitle', textKey: 'home.why.features.supportText', grad: 'from-sky-500 to-cyan-600' },
]

const STATS = [
    { valueKey: 'home.why.stats.secure',   labelKey: 'home.why.stats.secure' },
    { valueKey: 'home.why.stats.support',  labelKey: 'home.why.stats.support' },
    { valueKey: 'home.why.stats.delivery', labelKey: 'home.why.stats.delivery' },
    { valueKey: 'home.why.stats.warranty', labelKey: 'home.why.stats.warranty' },
]

// Stat values are language-neutral tokens (100% / 24/7 / Instant / Official).
const STAT_VALUES = ['100%', '24/7', 'home.why.stats.instant', 'home.why.stats.official']

// Admin stats item-ini "0" gizlətmə + enabled qaydası ilə göstərilə bilən listə çevirir.
const resolveAdminStats = (statsCfg) => {
    if (!statsCfg || statsCfg.enabled === false) return { hidden: true, items: [] }
    const items = (statsCfg.items || [])
        .filter(it => it.enabled !== false)
        .filter(it => {
            if (!statsCfg.hideZeroValues) return true
            const v = String(it.value ?? '').trim()
            return v !== '' && v !== '0' // "0" və boş dəyər gizlənir
        })
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map(it => ({ value: `${it.value ?? ''}${it.suffix ?? ''}`, label: it.label || '' }))
    return { hidden: false, items }
}

const WhyItemzo = () => {
    const { t } = useTranslation()
    const content = useHomepageContent()
    const admin = content?.stats ? resolveAdminStats(content.stats) : null
    // admin==null → hələ yüklənməyib/xəta → köhnə token fallback (regresiya yoxdur)
    const showAdminStats = admin && !admin.hidden
    const statsSectionHidden = admin && admin.hidden // enabled=false → bölmə gizli
    return (
    <section className='w-full max-w-marketplace mx-auto px-4 sm:px-5 md:px-6 lg:px-10 py-6'>
        <div className='text-center mb-7'>
            <div className='inline-flex items-center gap-1.5 rounded-pill bg-brand-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-700'>{t('home.why.eyebrow')}</div>
            <h2 className='mt-2 text-2xl sm:text-xl font-black text-slate-900'>{t('home.why.title')}</h2>
            <p className='mt-1.5 text-sm text-slate-500 max-w-xl mx-auto'>{t('home.why.subtitle')}</p>
        </div>

        <div className='grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4'>
            {FEATURES.map((f) => (
                <div key={f.titleKey} className='group rounded-card border border-slate-200 bg-white p-5 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-200'>
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${f.grad} text-white text-xl shadow group-hover:scale-105 transition-transform`}>
                        {f.icon}
                    </div>
                    <h3 className='mt-3 text-sm font-bold text-slate-800'>{t(f.titleKey)}</h3>
                    <p className='mt-1 text-[13px] text-slate-500 leading-relaxed'>{t(f.textKey)}</p>
                </div>
            ))}
        </div>

        {/* Stats — admin idarəli (enabled=false → gizli; "0"/boş dəyər gizlənir) */}
        {!statsSectionHidden && (
            <div className='mt-5 rounded-card border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-card'>
                {showAdminStats ? (
                    admin.items.length > 0 && (
                        <div className={`grid ${admin.items.length >= 4 ? 'grid-cols-4' : admin.items.length === 3 ? 'grid-cols-3' : admin.items.length === 2 ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2 divide-x divide-slate-100 sm:divide-x-0`}>
                            {admin.items.map((s, i) => (
                                <div key={i} className={`p-5 text-center ${i >= 2 ? 'sm:border-t sm:border-slate-100' : ''}`}>
                                    <p className='text-2xl sm:text-xl font-black bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent'>{s.value}</p>
                                    <p className='mt-1 text-xs font-semibold text-slate-500'>{s.label}</p>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className='grid grid-cols-4 sm:grid-cols-2 divide-x divide-slate-100 sm:divide-x-0'>
                        {STATS.map((s, i) => {
                            const raw = STAT_VALUES[i]
                            const value = raw.startsWith('home.') ? t(raw) : raw
                            return (
                                <div key={s.labelKey} className={`p-5 text-center ${i >= 2 ? 'sm:border-t sm:border-slate-100' : ''}`}>
                                    <p className='text-2xl sm:text-xl font-black bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent'>{value}</p>
                                    <p className='mt-1 text-xs font-semibold text-slate-500'>{t(s.labelKey)}</p>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )}
    </section>
    )
}

export default WhyItemzo
